import React from "react";
import {
  SelectDropdown,
  StandardSearchDropdown,
  FilterBar,
  ClearAllButton,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { useLessonReports } from "./useLessonReports";

const DATE_RANGES = [
  { value: "last_4_weeks", label: "Last 4 weeks" },
  { value: "last_8_weeks", label: "Last 8 weeks" },
  { value: "this_school_year", label: "This School Year" },
  { value: "all_time", label: "All Time" },
];

const byGradeThenLabel = (a, b) =>
  a.gradeRank - b.gradeRank || a.label.localeCompare(b.label, undefined, { numeric: true });

const gradeRank = (grade) => {
  if (grade == null) return 99;
  if (/^k/i.test(grade)) return 0;
  return parseInt(grade, 10) || 98;
};

const lessonLabel = (lesson) =>
  [lesson.ordinal_name, lesson.lesson_name].filter(Boolean).join(": ");

export const LessonReportFilters = ({ classes, filters, onFilterChange }) => {
  // Same query key as the card list, so this reads the cached response.
  // available_lessons ignores the lesson filter server-side; options stay
  // complete while a lesson is selected.
  const { availableLessons } = useLessonReports(filters);

  const classOptions = [
    { value: null, label: "All Classes", pinned: true },
    ...(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year })),
  ];

  // Client-side type-ahead over the already-fetched lessons; rows select by
  // lesson_id while displaying "ordinal: name" like the old dropdown.
  const searchLessons = (query) => {
    const q = (query || "").trim().toLowerCase();
    const results = availableLessons
      .map((lesson) => ({
        value: lesson.lesson_id,
        domain_code: lesson.ordinal_name,
        domain_name: lesson.lesson_name,
        standards: [],
        label: lessonLabel(lesson),
        gradeRank: gradeRank(lesson.course_grade_level),
      }))
      .filter((option) => !q || option.label.toLowerCase().includes(q))
      .sort(byGradeThenLabel);
    return { results };
  };

  const selectedClassName =
    filters.classroomId == null
      ? "All Classes"
      : classDisplayName(classes?.find((c) => String(c.id) === String(filters.classroomId))) || "All Classes";
  const selectedLesson = availableLessons.find((lesson) => lesson.lesson_id === filters.lessonId);
  const selectedDateLabel =
    DATE_RANGES.find((d) => d.value === filters.dateRange)?.label || "Last 4 weeks";

  return (
    <FilterBar>
      <SelectDropdown
        label="Class"
        value={filters.classroomId}
        displayText={selectedClassName}
        options={classOptions}
        groupBySchoolYear
        onChange={(classroomId) => onFilterChange({ ...filters, classroomId })}
        clearable
      />
      <StandardSearchDropdown
        label="Lesson"
        value={filters.lessonId ?? null}
        displayLabel={selectedLesson ? lessonLabel(selectedLesson) : "All Lessons"}
        placeholder="All Lessons"
        hideGroupHeaders
        fetchOptions={searchLessons}
        onChange={(lessonId) => onFilterChange({ ...filters, lessonId })}
      />
      <SelectDropdown
        label="Date Range of Last Session"
        value={filters.dateRange}
        displayText={selectedDateLabel}
        options={DATE_RANGES}
        onChange={(dateRange) => onFilterChange({ ...filters, dateRange })}
        clearable
        clearValue="all_time"
      />
      <ClearAllButton
        onClick={() =>
          onFilterChange({
            ...filters,
            classroomId: null,
            lessonId: null,
            dateRange: "all_time",
          })
        }
      >
        Clear All
      </ClearAllButton>
    </FilterBar>
  );
};
